/**
 * <mega-panels> — opens the mega menu panels of the header.
 *
 * Each panel (class "mega", rendered by a Mega menu block) declares the key of the main menu item it belongs to
 * (data-mega); every item of the desktop menu carries its key (data-key). For each panel, the script finds the item,
 * swaps the item's plain dropdown for a toggle button, and keeps the panel, which lives in this element under the
 * header bar, in step with it. One panel is open at a time:
 * - mouse: opens a moment after the pointer enters the item, closes a moment after it leaves both the item and
 *   the panel (so the pointer can travel between them);
 * - button: click or Enter / Space toggles it, and a keyboard activation moves the focus into the panel;
 * - keyboard: ArrowDown on the link or button opens the panel and enters it, Escape closes it and gives the focus
 *   back to the button, leaving the panel with Tab closes it;
 * - a pointer press outside closes it, and the theme editor opens the panel of the block being edited.
 * Without this script the item is a plain link and the panels stay hidden.
 */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 180;

class MegaPanels extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('.header') ?? document.body;
    this.entries = new Map();
    this.current = null;
    this.timer = null;

    for (const panel of this.querySelectorAll('.mega[data-mega]')) {
      const key = panel.dataset.mega;
      const item = this.header.querySelector(`.hnav__item[data-key="${CSS.escape(key)}"]`);
      if (!item || this.entries.has(key)) continue;

      const toggle = this.upgrade(item, panel);
      this.entries.set(key, { key, item, panel, toggle });

      item.addEventListener('pointerenter', (event) => this.onPointerEnter(event, key));
      item.addEventListener('pointerleave', this.onPointerLeave);
      panel.addEventListener('pointerenter', this.onPanelEnter);
      panel.addEventListener('pointerleave', this.onPointerLeave);
      toggle.addEventListener('click', (event) => this.toggle(key, event.detail === 0));
    }

    this.header.addEventListener('keydown', this.onKeydown);
    this.header.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('pointerdown', this.onPointerDownOutside);
    this.addEventListener('shopify:block:select', this.onEditorSelect);
    this.addEventListener('shopify:block:deselect', this.onEditorDeselect);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.header?.removeEventListener('keydown', this.onKeydown);
    this.header?.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('pointerdown', this.onPointerDownOutside);
  }

  /** Turns a menu item into a mega menu item: no dropdown, a toggle button after the link. */
  upgrade(item, panel) {
    const link = item.querySelector('.hnav__link');
    item.classList.remove('hnav__item--parent');
    item.classList.add('hnav__item--mega');
    item.querySelector('.hnav__sub')?.remove();

    const toggle = document.createElement('button');
    toggle.className = 'hnav__toggle';
    toggle.type = 'button';
    toggle.dataset.megaToggle = '';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', panel.id);
    toggle.setAttribute('aria-label', (this.dataset.toggleLabel ?? '').replace('__title__', link.textContent.trim()));
    toggle.innerHTML = '<span class="hnav__chevron" aria-hidden="true"></span>';
    link.after(toggle);
    return toggle;
  }

  schedule(callback, delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(callback, delay);
  }

  open(key, focusPanel = false) {
    const entry = this.entries.get(key);
    if (!entry) return;
    clearTimeout(this.timer);
    if (this.current && this.current !== key) this.close();

    this.current = key;
    entry.panel.inert = false;
    entry.panel.toggleAttribute('data-open', true);
    entry.item.toggleAttribute('data-open', true);
    entry.toggle.setAttribute('aria-expanded', 'true');

    if (focusPanel) entry.panel.querySelector('a[href]')?.focus();
  }

  close(returnFocus = false) {
    clearTimeout(this.timer);
    const entry = this.entries.get(this.current);
    if (!entry) return;

    this.current = null;
    entry.panel.inert = true;
    entry.panel.toggleAttribute('data-open', false);
    entry.item.toggleAttribute('data-open', false);
    entry.toggle.setAttribute('aria-expanded', 'false');

    if (returnFocus) entry.toggle.focus();
  }

  toggle(key, focusPanel) {
    if (this.current === key) this.close();
    else this.open(key, focusPanel);
  }

  onPointerEnter(event, key) {
    // Touch and pen use the button: a hover that follows a tap would reopen what the tap just closed.
    if (event.pointerType !== 'mouse') return;
    this.schedule(() => this.open(key), this.current ? 0 : OPEN_DELAY);
  }

  onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.schedule(() => this.close(), CLOSE_DELAY);
  };

  onPanelEnter = (event) => {
    if (event.pointerType === 'mouse') clearTimeout(this.timer);
  };

  onKeydown = (event) => {
    const entry = this.entries.get(this.current);

    if (event.key === 'Escape' && entry) {
      const focusInside = entry.item.contains(document.activeElement) || entry.panel.contains(document.activeElement);
      this.close(focusInside);
      return;
    }

    if (event.key === 'ArrowDown') {
      const item = event.target.closest?.('.hnav__item--mega');
      if (!item) return;
      event.preventDefault();
      this.open(item.dataset.key, true);
    }
  };

  onFocusOut = () => {
    // Wait for the focus to land: relatedTarget is empty when it goes to the page background.
    setTimeout(() => {
      const entry = this.entries.get(this.current);
      if (!entry) return;
      const active = document.activeElement;
      if (active === document.body) return;
      if (!entry.item.contains(active) && !entry.panel.contains(active)) this.close();
    });
  };

  onPointerDownOutside = (event) => {
    const entry = this.entries.get(this.current);
    if (!entry) return;
    if (!entry.item.contains(event.target) && !entry.panel.contains(event.target)) this.close();
  };

  onEditorSelect = (event) => {
    const panel = event.target.closest?.('.mega');
    if (panel) this.open(panel.dataset.mega);
  };

  onEditorDeselect = () => this.close();
}

if (!customElements.get('mega-panels')) customElements.define('mega-panels', MegaPanels);
